import React from 'react';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {Alerts, interpolate} from '../../constants/Strings';
import {heatProfilesApi} from '../api/apis';
import {
  heatProfilesMapSelector,
  deleteAccountHeatProfile as reduxDeleteAccountHeatProfile,
} from '../redux/slices/profilesSlice';
import {useAppDispatch} from '../redux/useAppDispatch';
import {HeatProfile} from '../types';
import {useAsyncFn} from './useAsyncFn';

interface DeleteHeatProfileOptions {
  id: string;
  onDelete?: () => void;
}

export const useDeleteAccountHeatProfile = () => {
  const profiles = useSelector(heatProfilesMapSelector);
  const dispatch = useAppDispatch();

  const [state, onConfirmPress] = useAsyncFn(
    async (heatProfile: HeatProfile) => {
      await heatProfilesApi
        .deleteHeatProfile({profileId: heatProfile.id})
        .catch(error => {
          Alert.alert(
            Alerts.PROFILE_DELETE_ERROR_TITLE,
            (error as Error).message,
          );

          throw error;
        });

      dispatch(reduxDeleteAccountHeatProfile(heatProfile.id));
    },
    [],
  );

  const deleteHeatProfile = React.useCallback(
    ({id, onDelete}: DeleteHeatProfileOptions) => {
      const heatProfile = profiles[id];

      if (!heatProfile) return;

      Alert.alert(
        Alerts.CONFIRM_DELETION,
        interpolate(Alerts.CONFIRM_DELETE_PROFILE_BODY, {
          profileName: heatProfile.name.toUpperCase(),
        }),
        [
          {text: 'Cancel', style: 'cancel'},
          {
            text: 'OK',
            onPress: () =>
              onConfirmPress(heatProfile).then(() => {
                onDelete?.();
              }),
          },
        ],
        {cancelable: false},
      );
    },
    [profiles, onConfirmPress],
  );

  return [state, deleteHeatProfile] as const;
};
